import {
    Box,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    Grid,
    Typography,
    alpha,
    useTheme,
    Stack,
} from '@mui/material';
import { CheckCircle as CheckIcon } from '@mui/icons-material';
import CategoryIcon from '@/components/CategoryIcon';
import { useI18nContext } from '@/i18n/i18n-react';

interface CategorySelectionCardProps {
    category: {
        id: number;
        name: string;
        icon: string;
    };
    isSelected: boolean;
    hasPendingRequest: boolean;
    onToggle: (id: number) => void;
}

export const CategorySelectionCard = ({
    category,
    isSelected,
    hasPendingRequest,
    onToggle,
}: CategorySelectionCardProps) => {
    const { LL } = useI18nContext();
    const theme = useTheme();

    return (
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card
                sx={{
                    position: 'relative',
                    border: isSelected ? `2px solid ${theme.palette.primary.main}` : '1px solid',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    bgcolor: isSelected
                        ? alpha(theme.palette.primary.main, 0.08)
                        : 'background.paper',
                    transition: 'all 0.2s',
                    opacity: hasPendingRequest ? 0.6 : 1,
                    '&:hover': hasPendingRequest
                        ? {}
                        : {
                              borderColor: 'primary.main',
                              boxShadow: 1,
                          },
                }}
            >
                <CardActionArea
                    onClick={() => !hasPendingRequest && onToggle(category.id)}
                    disabled={hasPendingRequest}
                    sx={{ height: '100%' }}
                >
                    <CardContent
                        sx={{
                            p: 1,
                            '&:last-child': { pb: 1 },
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            {/* Icon on the left */}
                            <Box
                                sx={{
                                    fontSize: '1.5rem',
                                    color: isSelected ? 'primary.main' : 'text.secondary',
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <CategoryIcon category={category.icon} />
                            </Box>

                            {/* Text on the right */}
                            <Stack
                                direction="row"
                                spacing={1}
                                sx={{ flex: 1, minWidth: 0, alignItems: 'center' }}
                            >
                                <Typography
                                    variant="body2"
                                    color={isSelected ? 'primary' : 'textPrimary'}
                                    sx={{
                                        fontWeight: isSelected ? 600 : 500,
                                        flex: 1,
                                        lineHeight: 1.3,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {category.name}
                                </Typography>
                                {hasPendingRequest && (
                                    <Chip
                                        label={LL.categoryRequests.pending()}
                                        variant="outlined"
                                        color="warning"
                                        size="small"
                                        sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                )}
                            </Stack>

                            {/* Checkmark */}
                            {isSelected && (
                                <CheckIcon
                                    color="primary"
                                    sx={{
                                        fontSize: 20,
                                        flexShrink: 0,
                                    }}
                                />
                            )}
                        </Box>
                    </CardContent>
                </CardActionArea>
            </Card>
        </Grid>
    );
};
